<template>
  <div class="ticket-preview-description" v-if="description">
    <div class="d-flex align-center">
      <v-icon icon="$ticketsXs" size="22" />
      <div class="subtitle1 ml-1">
        {{ $t("ticketDetails.textDescription.title") }}
      </div>
    </div>
    <div class="mt-2 body2 ticket-preview-description--text" v-html="description"></div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";
import { useI18n } from "vue-i18n";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { EDRTicketDescriptionCode, TicketTrigger, ThreatStatus } from "@/constants/tickets.ts";
import { flattenTicketDetails } from "@/_helpers/ticket-helpers";

export default defineComponent({
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const description = computed(() => {
      const sections = props?.ticketDetails?.sections;
      const ticketTrigger = props.ticketDetails.eventTrigger;
      // I need to flatten the ticket details to be able to easily use it in i18n
      const ticketObj = flattenTicketDetails(props.ticketDetails);
      const module = props.ticketDetails.moduleName;
      const isEdr = module === SubscriptionModule.EDR;
      const descriptionCode = sections?.edrMetaData?.descriptionCode;
      const asrDetectionReason = sections?.edrMetaData?.asrDetails?.detectionReason;
      const ticketTriggerSuffix =
        descriptionCode === EDRTicketDescriptionCode.ATTACK_USING_A_REMOTE_ACCESS_TOOL
          ? `.${ticketTrigger}`
          : "";

      const isQuarantined = sections?.malwareMetaData?.threatStatus === ThreatStatus.QUARANTINED;

      // Resolve the description key for triggers with variant-specific copy. Cloud-share
      // sensitive data shows the legacy description (with a single shared file name) by
      // default; the updated variant (no filesMetaData fileName) uses the base description.
      let resolvedTrigger: string = ticketTrigger;
      if (ticketTrigger === TicketTrigger.MALWARE_ON_ENDPOINT) {
        resolvedTrigger = isQuarantined
          ? TicketTrigger.MALWARE_ON_ENDPOINT
          : "malwareOnEndpointNotQuarantined";
      } else if (ticketTrigger === TicketTrigger.DLP_CLOUD_SENSITIVE_DATA) {
        resolvedTrigger = sections?.filesMetaData?.[0]?.fileName
          ? "dlpCloudSensitiveDataLegacy"
          : TicketTrigger.DLP_CLOUD_SENSITIVE_DATA;
      }

      let translationKey: string;
      if (asrDetectionReason) {
        translationKey = `ticketDetails.textDescription.${asrDetectionReason}`;
      } else if (isEdr && descriptionCode) {
        translationKey = `ticketDetails.textDescription.${descriptionCode}${ticketTriggerSuffix}`;
      } else {
        translationKey = `ticketDetails.textDescription.${resolvedTrigger}`;
      }

      // checking if translation exits, if not - we are hiding banner
      return i18n.te(translationKey)
        ? i18n.t(translationKey, {
            ...ticketObj,
          })
        : null;
    });

    return {
      description,
      SubscriptionModule,
    };
  },
});
</script>

<style scoped lang="scss">
.ticket-preview-description {
  padding: 15px;
  border-radius: 8px;
  background: rgb(var(--v-theme-purple-faint));
}

.ticket-preview-description--text {
  line-height: 24px !important;
}
</style>
